import { CartUpdateEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

let changingSize = false;
const propertiesKey = properties => JSON.stringify(Object.entries(properties || {}).sort(([a], [b]) => a.localeCompare(b)));
function sameLine(item, reference, variant = reference.variant_id) {
  return item.variant_id === variant && propertiesKey(item.properties) === propertiesKey(reference.properties)
    && item.selling_plan_allocation?.selling_plan.id === reference.selling_plan_allocation?.selling_plan.id;
}
async function cartRequest(path, body) {
  const root = window.Shopify?.routes?.root || '/';
  const response = await fetch(root + path, {
    method: body ? 'POST' : 'GET',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const result = await response.json();
  if (!response.ok || result.errors) throw new Error(result.description || result.message || 'Cart update failed');
  return result;
}
if (!customElements.get('pf-cart-size-editor')) {
  customElements.define('pf-cart-size-editor', class extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('select');
      // Bind once per select: rows are moved and re-rendered, and each reconnect used to add one more listener.
      if (!this.select || this.boundSelect === this.select) return;
      this.boundSelect = this.select;
      this.select.addEventListener('change', () => this.changeSize());
    }
    // The line's variant comes from the server-rendered markup (kept current by re-renders), never from state cached in JS.
    get current() { return this.dataset.variantId || this.select?.querySelector('option[selected]')?.value || ''; }
    async changeSize() {
      if (changingSize) { this.select.value = this.current; return; }
      const nextVariant = Number(this.select.value);
      const currentVariant = this.current;
      if (!currentVariant || String(nextVariant) === currentVariant) { this.select.value = currentVariant; return; }
      changingSize = true;
      const component = this.closest('cart-items-component');
      const sectionId = component?.dataset.sectionId;
      const displayedKeys = [...(component?.querySelectorAll('tr[data-key]') || [])].map(row => row.dataset.key);
      const error = this.querySelector('[role="alert"]');
      error.hidden = true;
      const controls = [...document.querySelectorAll('cart-items-component select, cart-items-component button, cart-items-component input')];
      const disabledBefore = controls.map(control => control.disabled);
      controls.forEach(control => { control.disabled = true; });
      this.setAttribute('aria-busy', 'true');
      if (component) { component.style.transition = 'opacity .15s'; component.style.opacity = '.45'; }
      let added;
      let previous;
      let previousNextQuantity = 0;
      try {
        const cart = await cartRequest('cart.js');
        previous = cart.items.find(item => item.key === this.dataset.key);
        // Only ever change the line this select belongs to, and only from the size it really has in the cart.
        if (!previous || String(previous.variant_id) !== currentVariant) throw new Error('Line changed');
        const gifts = cart.items.filter(line => line.parent_relationship?.parent_key === previous.key);
        previousNextQuantity = cart.items.find(line => sameLine(line, previous, nextVariant))?.quantity || 0;
        const item = { id: nextVariant, quantity: previous.quantity, properties: previous.properties || {} };
        if (previous.selling_plan_allocation) item.selling_plan = previous.selling_plan_allocation.selling_plan.id;
        // Fast path: new size + its free accessories in one request, then remove the old line by key.
        const giftItems = gifts.map(gift => ({ id: gift.variant_id, quantity: gift.quantity, properties: gift.properties || {}, parent_id: nextVariant }));
        const result = await cartRequest('cart/add.js', { items: [item, ...giftItems] });
        added = result.items[0];
        let updated;
        try {
          updated = await cartRequest('cart/change.js', { id: previous.key, quantity: 0,
            ...(sectionId ? { sections: [sectionId], sections_url: location.pathname } : {}) });
        } catch {
          // Keys can change after a discount recalculation: resolve the old line again.
          const latest = await cartRequest('cart.js');
          const previousIndex = latest.items.findIndex(line => sameLine(line, previous));
          if (previousIndex < 0) throw new Error('Previous line missing');
          updated = await cartRequest('cart/change.js', { line: previousIndex + 1, quantity: 0,
            ...(sectionId ? { sections: [sectionId], sections_url: location.pathname } : {}) });
        }
        added = null;
        // Shopify prepends the replacement variant. Keep the visible row order,
        // while preserving the server's line indexes for quantity/remove actions.
        const orderedKeys = displayedKeys.map(key => {
          const original = cart.items.find(line => line.key === key);
          if (!original) return null;
          return updated.items.find(line => sameLine(line, original,
            original.key === previous.key ? nextVariant : original.variant_id))?.key;
        }).filter(Boolean);
        for (const [id, html] of Object.entries(updated.sections || {})) {
          if (!html) continue;
          const documentCopy = new DOMParser().parseFromString(html, 'text/html');
          for (const body of documentCopy.querySelectorAll('cart-items-component tbody')) {
            const rows = [...body.querySelectorAll(':scope > tr[data-key]')];
            const byKey = new Map(rows.map(row => [row.dataset.key, row]));
            const ordered = orderedKeys.map(key => byKey.get(key)).filter(Boolean);
            const remaining = rows.filter(row => !ordered.includes(row));
            [...ordered, ...remaining].forEach(row => body.append(row));
          }
          updated.sections[id] = documentCopy.body.innerHTML;
        }
        document.dispatchEvent(new CartUpdateEvent(updated, 'pf-cart-size-editor', {
          source: 'pf-cart-size-editor', itemCount: updated.item_count, sections: updated.sections,
        }));
        this.dataset.variantId = String(nextVariant);
      } catch (failure) {
        if (added && previous) {
          try {
            const latest = await cartRequest('cart.js');
            const index = latest.items.findIndex(line => sameLine(line, previous, nextVariant));
            if (index >= 0) await cartRequest('cart/change.js', { line: index + 1, quantity: previousNextQuantity });
          } catch { location.reload(); return; }
        }
        this.select.value = this.current;
        error.textContent = this.dataset.error;
        error.hidden = false;
        if (failure?.message === 'Line changed' && sectionId) sectionRenderer.renderSection(sectionId, { cache: false }).catch(() => {});
      } finally {
        changingSize = false;
        controls.forEach((control, index) => { control.disabled = disabledBefore[index]; });
        this.removeAttribute('aria-busy');
        const live = document.querySelector(`cart-items-component[data-section-id="${sectionId}"]`) || component; if (live) live.style.opacity = '';
        if (component) component.style.opacity = '';
      }
    }
  });
}
